import React from "react";
import { LegalShell, LegalSection } from "./LegalShell";

export default function Privacy() {
  return (
    <LegalShell title="PRIVACY POLICY" subtitle="How We Handle Your Data">
      <p>
        This Privacy Policy describes how <strong className="text-white">GP Picks</strong> ("we", "us") collects, uses,
        and protects information when you use the Service at gppicks.com.
      </p>

      <LegalSection title="1. What We Collect">
        <p>We collect the minimum data needed to run the Service:</p>
        <ul className="list-disc list-inside pl-2 text-white/70">
          <li><strong className="text-white">Account:</strong> email address, bcrypt-hashed password, account creation date.</li>
          <li><strong className="text-white">Payment:</strong> processed entirely by <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="text-[#FF4500] hover:underline">Stripe</a>. We do NOT store card numbers or CVCs. We store a Stripe customer ID, subscription status, and renewal date only.</li>
          <li><strong className="text-white">Usage:</strong> which picks you save to "My Picks" (tied to your account), page views for anonymous analytics, and basic server logs (IP address, browser type, timestamps).</li>
          <li><strong className="text-white">Cookies:</strong> a single httpOnly cookie (<code>access_token</code>) used to keep you signed in. No third-party ad tracking cookies.</li>
        </ul>
      </LegalSection>

      <LegalSection title="2. What We Do NOT Collect">
        <ul className="list-disc list-inside pl-2 text-white/70">
          <li>Government IDs or social security numbers</li>
          <li>Bank account or credit card details (Stripe holds these)</li>
          <li>Location data beyond what your IP address reveals</li>
          <li>Contact lists, photos, microphone, or camera data</li>
        </ul>
      </LegalSection>

      <LegalSection title="3. How We Use It">
        <ul className="list-disc list-inside pl-2 text-white/70">
          <li>To authenticate you and keep your account secure.</li>
          <li>To process your subscription (via Stripe).</li>
          <li>To personalize the "My Picks" watchlist feature.</li>
          <li>To send transactional email (receipts, password resets, subscription changes). We do NOT send marketing email unless you opt in.</li>
          <li>To operate and improve the Service (anonymous aggregate analytics only).</li>
          <li>To comply with legal obligations (e.g., tax records for Stripe payouts).</li>
        </ul>
      </LegalSection>

      <LegalSection title="4. Who We Share Data With">
        <p>We share data only with the service providers required to operate:</p>
        <ul className="list-disc list-inside pl-2 text-white/70">
          <li><strong className="text-white">Stripe</strong> — payment processing. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#FF4500] hover:underline">Stripe's Privacy Policy</a>.</li>
          <li><strong className="text-white">MongoDB Atlas</strong> — encrypted database hosting.</li>
          <li><strong className="text-white">Emergent Cloud</strong> — application hosting on Kubernetes.</li>
        </ul>
        <p>
          <strong className="text-white">We never sell your personal data.</strong> We do not share it with advertisers, data brokers,
          or affiliates.
        </p>
        <p>
          We may disclose data if legally compelled by a valid court order, subpoena, or law enforcement request from a
          jurisdiction with authority over us.
        </p>
      </LegalSection>

      <LegalSection title="5. Data Retention">
        <p>
          Account and subscription data is retained while your account is active and for up to 24 months after cancellation
          for tax/audit purposes. Ephemeral data (pick_results TTL, server logs) is auto-deleted after 48 hours to 30 days.
        </p>
      </LegalSection>

      <LegalSection title="6. Your Rights">
        <p>Depending on your jurisdiction (GDPR, CCPA, PIPEDA, etc.), you may have the right to:</p>
        <ul className="list-disc list-inside pl-2 text-white/70">
          <li>Access the personal data we hold about you</li>
          <li>Correct inaccurate data</li>
          <li>Request deletion of your account and associated data</li>
          <li>Export your data in a portable format</li>
          <li>Opt out of any non-essential processing</li>
        </ul>
        <p>To exercise these rights, email <a href="mailto:privacy@gppicks.com" className="text-[#FF4500] hover:underline">privacy@gppicks.com</a>. We respond within 30 days.</p>
      </LegalSection>

      <LegalSection title="7. Security">
        <p>
          We use industry-standard measures — bcrypt password hashing, JWT with short expirations, httpOnly cookies, TLS
          everywhere, and encrypted database at rest — to protect your data. No system is 100% secure. If we ever detect a
          breach that affects you, we will notify you within 72 hours as required by applicable law.
        </p>
      </LegalSection>

      <LegalSection title="8. Children">
        <p>
          The Service is not directed to anyone under 18. We do not knowingly collect data from minors. If we learn we have
          collected data from a minor, we will delete it immediately.
        </p>
      </LegalSection>

      <LegalSection title="9. International Transfers">
        <p>
          Our infrastructure is hosted in North America. If you access the Service from outside North America, your data
          will be transferred to and processed in North America under the safeguards described in this Policy.
        </p>
      </LegalSection>

      <LegalSection title="10. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be posted here with an updated "Last
          updated" date and, for existing subscribers, an email notification.
        </p>
      </LegalSection>

      <LegalSection title="11. Contact">
        <p>
          Questions about this Policy or your data? Message us on Instagram: <a href="https://instagram.com/gppickdotcom" target="_blank" rel="noopener noreferrer" className="text-[#FF4500] hover:underline">@gppickdotcom</a>.
        </p>
      </LegalSection>
    </LegalShell>
  );
}
